/**
 * Geodetic constants and helpers (WGS84).
 *
 * The full geodetic ↔ ENU machinery and the floating-origin rebasing land with the terrain
 * milestone. What is fixed here now are the constants and the *contract* the rest of the
 * engine will be written against, because getting the precision policy right retroactively is
 * expensive:
 *
 *   - Sim space is geodetic (latitude, longitude, altitude) plus a local ENU frame, all in
 *     double precision. JS numbers are doubles, so this costs nothing.
 *   - Render space is FP32, because that is what the GPU takes. At 1 m precision FP32 runs out
 *     of significant bits around 8 000 km from the origin, and visible jitter starts far
 *     earlier — hence non-negotiable #4: rebase the origin whenever the aircraft is more than
 *     {@link ORIGIN_REBASE_DISTANCE_M} from it.
 */

/** WGS84 semi-major axis [m]. */
export const WGS84_SEMI_MAJOR_AXIS_M = 6_378_137.0;
/** WGS84 flattening [-]. */
export const WGS84_FLATTENING = 1 / 298.257223563;
/** WGS84 semi-minor axis [m]. */
export const WGS84_SEMI_MINOR_AXIS_M = WGS84_SEMI_MAJOR_AXIS_M * (1 - WGS84_FLATTENING);
/** First eccentricity squared [-]. */
export const WGS84_ECCENTRICITY_SQ = WGS84_FLATTENING * (2 - WGS84_FLATTENING);
/** Mean Earth radius [m], for great-circle work where the ellipsoid is overkill. */
export const EARTH_MEAN_RADIUS_M = 6_371_008.8;

/**
 * Rebase the render origin once the aircraft is further than this from it [m].
 *
 * ## Why 500 m and not 10 km
 *
 * It was 10 km, on the reasoning that 10 km keeps FP32 coordinates under ~2^14 m, where a float
 * still resolves well under a millimetre and the cockpit does not shake. All of that is true, and
 * it is an argument for an *upper* bound — it says nothing about what the threshold costs, and the
 * cost turned out to be the single worst frame in the simulator.
 *
 * A rebase translates every object in the scene at once, and the frame that follows is expensive in
 * a way that scales with *how far* they moved rather than with how many of them there are. Dirtying
 * every visible tile's matrix by 0.1 mm costs 1.5 ms; moving them 10 km costs 350–730 ms. Measured
 * on the canyon run at 500 kt by sweeping this constant at runtime, with the aircraft flying and
 * the gun firing:
 *
 * | threshold | rebases | mean frame | worst frame | cost per 10 km flown |
 * | --- | --- | --- | --- | --- |
 * | 10,000 m | 2 in 60 s | 559 ms | 727 ms | 559 ms |
 * | 4,000 m | 4 in 60 s | 58 ms | 65 ms | 145 ms |
 * | 2,000 m | 4 in 25 s | 19 ms | 35 ms | 95 ms |
 * | 1,000 m | 6 in 25 s | 9 ms | 16 ms | 90 ms |
 * | **500 m** | 8 in 15 s | **6 ms** | **16 ms** | 120 ms |
 * | 250 m | 16 in 15 s | 4 ms | 4 ms | 160 ms |
 *
 * The collapse is superlinear, which is the surprising part and the reason this is a *smaller*
 * number rather than a scheme for spreading one big rebase over several frames: rebasing twenty
 * times as often is about five times cheaper in total, not twenty times more expensive. Nothing
 * about the origin's contract changes — each rebase is still atomic, and every consumer that
 * assumed atomicity still gets it.
 *
 * Precision moves the same way. At 500 m an FP32 coordinate resolves to about 30 µm against the
 * 1 mm the old bound argued for, so the cockpit is further from shaking than it was, not closer.
 *
 * ## Why 500 m and not 250 m
 *
 * Because 250 m was tried, on a full 60 s canyon run each, and is worse:
 *
 * | | 10,000 m | 500 m | 250 m |
 * | --- | --- | --- | --- |
 * | worst frame in 60 s | 771 ms | **41.9 ms** | 65.1 ms |
 * | worst rebase frame | 771 ms | 41.9 ms | 65.1 ms |
 * | 1 %-low | 30.0 fps | 39.7 fps | 40.2 fps |
 * | worst GC gap | 25.4 ms | 6.6 ms | 6.1 ms |
 * | major collections | 7 | 7 | 7 |
 *
 * The sweep above says 250 m should be a flat 4 ms, and in a *fresh* run it is not. That reading
 * was taken by lowering the threshold part-way through a flight, so every rebase it measured
 * happened in terrain three had already built bind groups for. A run that starts at 250 m moves
 * into an uncached spatial configuration every second, and the first-use cost of that is a
 * different problem from the distance one — which is worth stating because it is the ceiling on
 * this approach. Below about 500 m the distance term is already gone and all that is left is
 * per-rebase first-use work, so going lower buys nothing and pays more of it.
 *
 * At 500 m a 500 kt run rebases every 1.9 s and a circuit at 110 kt every 8.8 s.
 */
export const ORIGIN_REBASE_DISTANCE_M = 500;

/** Metres per degree of latitude at `latitudeDeg`, on the WGS84 ellipsoid. */
export function metresPerDegreeLatitude(latitudeDeg: number): number {
  const latRad = (latitudeDeg * Math.PI) / 180;
  const sinLat = Math.sin(latRad);
  const denominator = Math.pow(1 - WGS84_ECCENTRICITY_SQ * sinLat * sinLat, 1.5);
  const meridionalRadius =
    (WGS84_SEMI_MAJOR_AXIS_M * (1 - WGS84_ECCENTRICITY_SQ)) / denominator;
  return (Math.PI / 180) * meridionalRadius;
}

/** Metres per degree of longitude at `latitudeDeg`, on the WGS84 ellipsoid. */
export function metresPerDegreeLongitude(latitudeDeg: number): number {
  const latRad = (latitudeDeg * Math.PI) / 180;
  const sinLat = Math.sin(latRad);
  const primeVerticalRadius =
    WGS84_SEMI_MAJOR_AXIS_M / Math.sqrt(1 - WGS84_ECCENTRICITY_SQ * sinLat * sinLat);
  return (Math.PI / 180) * primeVerticalRadius * Math.cos(latRad);
}

/** Great-circle distance between two geodetic points [m] (haversine, spherical Earth). */
export function haversineDistanceM(
  latitudeDegA: number,
  longitudeDegA: number,
  latitudeDegB: number,
  longitudeDegB: number,
): number {
  const toRad = Math.PI / 180;
  const dLat = (latitudeDegB - latitudeDegA) * toRad;
  const dLon = (longitudeDegB - longitudeDegA) * toRad;
  const latA = latitudeDegA * toRad;
  const latB = latitudeDegB * toRad;
  const sinHalfLat = Math.sin(dLat / 2);
  const sinHalfLon = Math.sin(dLon / 2);
  const a = sinHalfLat * sinHalfLat + Math.cos(latA) * Math.cos(latB) * sinHalfLon * sinHalfLon;
  return 2 * EARTH_MEAN_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** True bearing from A to B [rad], measured clockwise from north. */
export function initialBearingRad(
  latitudeDegA: number,
  longitudeDegA: number,
  latitudeDegB: number,
  longitudeDegB: number,
): number {
  const toRad = Math.PI / 180;
  const latA = latitudeDegA * toRad;
  const latB = latitudeDegB * toRad;
  const dLon = (longitudeDegB - longitudeDegA) * toRad;
  const y = Math.sin(dLon) * Math.cos(latB);
  const x = Math.cos(latA) * Math.sin(latB) - Math.sin(latA) * Math.cos(latB) * Math.cos(dLon);
  const bearing = Math.atan2(y, x);
  return bearing < 0 ? bearing + Math.PI * 2 : bearing;
}

/**
 * The distance actually in force, which is {@link ORIGIN_REBASE_DISTANCE_M} unless overridden.
 *
 * Overridable **for diagnosis only**, through `?rebase=<metres>`. A rebase costs one long frame —
 * 19.5 ms against an 8.4 ms median, measured — and its *frequency* is exactly proportional to
 * airspeed: every 3.2 s at 300 kt, every 1.9 s at 480. That makes it the one candidate for a
 * periodic disturbance whose rate rises with speed and which is identical in every camera view,
 * because frame pacing does not care where the camera is.
 *
 * A pilot reported jitter starting around 480 kt, in all views, which survived four separate
 * geometric fixes and twenty-eight refuted hypotheses. Rather than guess again, this makes the
 * question answerable in one page load: fly the same profile at `?rebase=500` and `?rebase=8000`
 * and see whether the jitter thins out with the rebases. It is a knob for an experiment, not a
 * setting — raising it makes each rebase far more expensive, because the cost scales
 * superlinearly with how far the world moves (500 m ≈ 1.2 ms of object motion, 1 km ≈ 21 ms,
 * 11 km ≈ 331 ms), which is exactly why 500 m was chosen.
 */
let activeRebaseDistanceM = ORIGIN_REBASE_DISTANCE_M;

/** The rebase threshold in force [m]. */
export function originRebaseDistanceM(): number {
  return activeRebaseDistanceM;
}

/**
 * Override the rebase threshold, for the `?rebase=` diagnostic. Clamped to a sane band so a typo
 * cannot produce either a rebase every frame or FP32 jitter from never rebasing at all.
 */
export function setOriginRebaseDistanceM(metres: number): void {
  if (!Number.isFinite(metres)) return;
  activeRebaseDistanceM = Math.min(50_000, Math.max(100, metres));
}

/** Does this position warrant a floating-origin rebase? */
export function needsOriginRebase(eastM: number, northM: number, upM: number): boolean {
  const limit = activeRebaseDistanceM;
  return eastM * eastM + northM * northM + upM * upM > limit * limit;
}
